import { useCallback, useEffect, useRef, useState } from 'react';
import { button, folder } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import type { DatumEngine } from '@datum-sdk/engine';

type ChangeContext = { initial?: boolean };

/** Local mirror of the SDK ColliderConfig fields the panel edits. */
interface PanelCollider {
  id: string;
  shape: string;
  role: 'obstacle' | 'containment';
  position: [number, number, number];
  quaternion: [number, number, number, number];
  scale: [number, number, number];
}

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;

/** Y-axis rotation (deg) from a quaternion — all Pilgrim OBBs are Y-only. */
const quatToYDeg = (q: [number, number, number, number]): number =>
  +(2 * Math.atan2(q[1], q[3]) * RAD_TO_DEG).toFixed(1);

const yDegToQuat = (deg: number): [number, number, number, number] => {
  const half = (deg * DEG_TO_RAD) / 2;
  return [0, +Math.sin(half).toFixed(10), 0, +Math.cos(half).toFixed(10)];
};

const copySdkForm = (c: PanelCollider, label: string): void => {
  const json = JSON.stringify(
    {
      id: c.id,
      shape: c.shape,
      role: c.role,
      transform: { position: c.position, quaternion: c.quaternion, scale: c.scale },
    },
    null,
    2,
  );
  navigator.clipboard?.writeText(json);
  console.log(`[SplatDevPanel] ${label}:\n${json}`);
};

/**
 * Colliders block for the SDK dev panel. Seeds a session-local list from the
 * live engine (`exportColliderConfig`) and edits it through the runtime
 * collider APIs — `updateColliderTransform` is the canonical live path (the
 * engine explicitly warns against mutating `getColliderObject` directly), all
 * mutators wake the render loop themselves. Copy buttons output the SDK-form
 * JSON ready to paste into `nativeScenes.ts` collider arrays.
 */
export const useSplatColliders = (
  engine: DatumEngine | null,
  /** Called after add/remove so the panel can re-apply the highlight style. */
  onSetMutated: () => void,
): { buildSchema: () => Schema; schemaDeps: unknown[] } => {
  const [colliders, setColliders] = useState<PanelCollider[]>([]);
  const collidersRef = useRef(colliders);
  collidersRef.current = colliders;

  const seed = useCallback((): void => {
    if (!engine) {
      setColliders([]);
      return;
    }
    const exported = engine.exportColliderConfig();
    setColliders(
      exported.map((c) => {
        const t = c.transform ?? {};
        const scale = Array.isArray(t.scale)
          ? (t.scale as [number, number, number])
          : ([t.scale ?? 1, t.scale ?? 1, t.scale ?? 1] as [number, number, number]);
        return {
          id: String(c.id ?? `collider-${Math.floor(Math.random() * 1e6)}`),
          shape: String(c.shape ?? 'box'),
          role: (c.role ?? 'obstacle') as PanelCollider['role'],
          position: (t.position ?? [0, 0, 0]) as [number, number, number],
          quaternion: (t.quaternion ?? [0, 0, 0, 1]) as [number, number, number, number],
          scale,
        };
      }),
    );
  }, [engine]);

  useEffect(() => {
    seed();
  }, [seed]);

  const applyTransform = useCallback(
    (c: PanelCollider): void => {
      engine?.updateColliderTransform(c.id, {
        position: c.position,
        quaternion: c.quaternion,
        scale: c.scale,
      });
    },
    [engine],
  );

  const patchCollider = useCallback(
    (id: string, patch: Partial<PanelCollider>): void => {
      setColliders((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          const next = { ...c, ...patch };
          if (patch.role && patch.role !== c.role) {
            engine?.updateColliderRole(id, patch.role);
          } else {
            applyTransform(next);
          }
          return next;
        }),
      );
    },
    [engine, applyTransform],
  );

  const addCollider = useCallback(
    (shape: 'box' | 'cylinder' | 'sphere', role: PanelCollider['role']): void => {
      if (!engine) return;
      const cam = engine.getCameraState();
      // Legacy spawn semantics: obstacles at the camera, containment at the pivot.
      const at = role === 'obstacle' ? cam.position : cam.lookAt;
      const scale: [number, number, number] =
        shape === 'box'
          ? role === 'obstacle'
            ? [0.3, 0.3, 0.3]
            : [4, 4, 4]
          : shape === 'cylinder'
            ? [6, 2, 6] // [diameter, height, diameter] — legacy r=3 h=2
            : [0.3, 0.3, 0.3]; // sphere: uniform diameter
      const id = `${shape}-${Date.now()}`;
      void engine
        .addCollider({
          id,
          shape,
          role,
          transform: {
            position: [at[0], at[1], at[2]],
            quaternion: [0, 0, 0, 1],
            scale,
          },
        })
        .then(() => {
          seed();
          onSetMutated();
        });
    },
    [engine, seed, onSetMutated],
  );

  const removeCollider = useCallback(
    (id: string): void => {
      engine?.removeCollider(id);
      seed();
      onSetMutated();
    },
    [engine, seed, onSetMutated],
  );

  const buildSchema = useCallback((): Schema => {
    const schema: Schema = {
      'Add Box (obstacle)': button(() => addCollider('box', 'obstacle')),
      'Add Box (containment)': button(() => addCollider('box', 'containment')),
      'Add Cylinder (containment)': button(() => addCollider('cylinder', 'containment')),
      'Copy All': button(() => {
        const all = collidersRef.current.map((c) => ({
          id: c.id,
          shape: c.shape,
          role: c.role,
          transform: { position: c.position, quaternion: c.quaternion, scale: c.scale },
        }));
        const json = JSON.stringify(all, null, 2);
        navigator.clipboard?.writeText(json);
        console.log(`[SplatDevPanel] All colliders:\n${json}`);
      }),
    };

    for (const c of collidersRef.current) {
      const f: Schema = {};
      const num = (
        key: string,
        label: string,
        value: number,
        write: (v: number, cur: PanelCollider) => Partial<PanelCollider>,
      ) => {
        f[`${c.id}-${key}`] = {
          label,
          value: +value.toFixed(3),
          step: 0.01,
          onChange: (v: number, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            const cur = collidersRef.current.find((x) => x.id === c.id);
            if (cur) patchCollider(c.id, write(v, cur));
          },
        };
      };
      num('px', 'Pos X', c.position[0], (v, cur) => ({ position: [v, cur.position[1], cur.position[2]] }));
      num('py', 'Pos Y', c.position[1], (v, cur) => ({ position: [cur.position[0], v, cur.position[2]] }));
      num('pz', 'Pos Z', c.position[2], (v, cur) => ({ position: [cur.position[0], cur.position[1], v] }));
      // SDK scale semantics: box = FULL size; cylinder = [diameter, height,
      // diameter]; sphere = diameter (uniform).
      num('sx', c.shape === 'cylinder' ? 'Diameter' : 'Scale X', c.scale[0], (v, cur) => ({
        scale:
          c.shape === 'sphere' ? [v, v, v] : c.shape === 'cylinder' ? [v, cur.scale[1], v] : [v, cur.scale[1], cur.scale[2]],
      }));
      if (c.shape !== 'sphere') {
        num('sy', c.shape === 'cylinder' ? 'Height' : 'Scale Y', c.scale[1], (v, cur) => ({
          scale: [cur.scale[0], v, cur.scale[2]],
        }));
      }
      if (c.shape === 'box') {
        num('sz', 'Scale Z', c.scale[2], (v, cur) => ({
          scale: [cur.scale[0], cur.scale[1], v],
        }));
        f[`${c.id}-rotY`] = {
          label: 'Rot Y (°)',
          value: quatToYDeg(c.quaternion),
          min: -180,
          max: 360,
          step: 1,
          onChange: (v: number, _p: string, ctx: ChangeContext) => {
            if (ctx.initial) return;
            patchCollider(c.id, { quaternion: yDegToQuat(v) });
          },
        };
      }
      f[`${c.id}-role`] = {
        label: 'Role',
        options: { obstacle: 'obstacle', containment: 'containment' },
        value: c.role,
        onChange: (v: PanelCollider['role'], _p: string, ctx: ChangeContext) => {
          if (ctx.initial) return;
          patchCollider(c.id, { role: v });
        },
      };
      f[`Copy##${c.id}`] = button(() => {
        const cur = collidersRef.current.find((x) => x.id === c.id);
        if (cur) copySdkForm(cur, cur.id);
      });
      f[`Delete##${c.id}`] = button(() => removeCollider(c.id));

      schema[`${c.id} (${c.shape})`] = folder(f, { collapsed: true });
    }
    return schema;
  }, [colliders, addCollider, removeCollider, patchCollider]);

  return { buildSchema, schemaDeps: [buildSchema] };
};
